/**
 * /producao/otimizacao — Otimização & Lotes (Sprint P2).
 *
 * Princípio da tela (brief 0.11): a produção NUNCA trava sem PharuCut —
 * dois caminhos: otimizar via provider (mock por enquanto) ou registrar o
 * lote otimizado externamente (o ERP é o sistema de REGISTRO).
 *
 * 3 zonas:
 *   Zona 1 — EntityHeader + botão primário "Criar lote com selecionados"
 *            (desabilitado sem seleção).
 *   Zona 2 — Bloco POOL DE ELEGÍVEIS: cards compact com checkbox de
 *            multi-seleção (elegível = Aprovação concluída + sem lote ativo).
 *   Zona 3 — Bloco LOTES: tabela fixa (sem scroll lateral), linha clicável
 *            para /producao/lotes/{id}.
 *
 * Fluxo criar: seleção -> slide-over de confirmação (ambientes + origem
 * EXTERNO default | DASHCUT mock) -> POST create-com-ambientes -> navega
 * para o detalhe do lote criado.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import AmbienteCard from "../../components/producao/AmbienteCard";
import { useAuth } from "../../context/AuthContext";
import {
  FASE_INFO,
  ORIGENS_LOTE,
  SITUACAO_LOTE_LABELS,
  SITUACAO_LOTE_TONES,
  ambientesElegiveis,
  criarLote,
  listarLotes,
} from "../../services/producaoV2";
import { formatarDataHoraCurta } from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
} from "../Orcamentos/styles";

const GradePool = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

/* Card selecionável: o clique no card TOGGLA a seleção (não navega) e o
   checkbox no canto espelha o estado — alvo generoso, padrão de pool. */
const CardSelecionavel = styled.div`
  position: relative;
  border-radius: 14px;

  ${({ $selecionado }) =>
    $selecionado
      ? `
        outline: 2px solid var(--accent);
        outline-offset: 1px;
        border-radius: 13px;
      `
      : ""}

  /* Checkbox "flutuante" no canto do card (levemente para fora) — não
     cobre o selo de exceção (Congelado) da faixa de contexto. */
  input[type="checkbox"] {
    position: absolute;
    top: -7px;
    right: -7px;
    z-index: 2;
    width: 18px;
    height: 18px;
    accent-color: var(--accent);
    cursor: pointer;
    background: var(--surface);
    border-radius: 4px;
    box-shadow: var(--shadow-1);
  }
`;

const BarraSelecao = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ContadorSelecao = styled.span`
  font-size: 12px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
`;

const TabelaLotes = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 10px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover {
    background: var(--surface-hover);
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const ListaConfirmacao = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    font-size: 13px;
    color: var(--ink-2);
    padding: 8px 10px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    background: var(--surface-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CampoForm = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const RotuloCampo = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

const DicaCampo = styled.p`
  font-size: 12px;
  color: var(--ink-3);
  margin: 4px 0 0;
`;

const RodapePainel = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const LinhaErro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export default function Otimizacao() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();

  const [pool, setPool] = useState(null); // {ambientes, total}
  const [lotes, setLotes] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [confirmandoCriacao, setConfirmandoCriacao] = useState(false);
  const [origem, setOrigem] = useState("EXTERNO");

  const podeOtimizar = hasPermission("lote:otimizar");

  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      const [p, l] = await Promise.all([
        ambientesElegiveis(request),
        listarLotes(request),
      ]);
      setPool(p);
      setLotes(Array.isArray(l) ? l : l?.results || []);
      // Poda seleção de ambientes que saíram do pool (viraram lote etc).
      const idsValidos = new Set((p?.ambientes || []).map((a) => a.id));
      setSelecionados(
        (prev) => new Set([...prev].filter((id) => idsValidos.has(id))),
      );
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar a tela.");
    } finally {
      setCarregando(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const alternarSelecao = (id) =>
    setSelecionados((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  const ambientesPool = useMemo(() => pool?.ambientes || [], [pool]);
  const ambientesSelecionados = useMemo(
    () => ambientesPool.filter((a) => selecionados.has(a.id)),
    [ambientesPool, selecionados],
  );

  const abrirConfirmacao = () => {
    setOrigem("EXTERNO");
    setConfirmandoCriacao(true);
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Otimização & Lotes"
        subtitle="Produção / Otimização — agrupar ambientes em lote de corte"
        primaryAction={
          podeOtimizar
            ? {
                label: "Criar lote com selecionados",
                onClick: abrirConfirmacao,
                disabled: selecionados.size === 0,
                title:
                  selecionados.size === 0
                    ? "Selecione ao menos um ambiente no pool"
                    : undefined,
              }
            : undefined
        }
      />

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>{erro}</ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      <Card>
        <BarraSelecao>
          <SectionTitle>Pool de ambientes elegíveis</SectionTitle>
          <ContadorSelecao>
            {selecionados.size > 0
              ? `${selecionados.size} selecionado(s) de ${ambientesPool.length}`
              : `${ambientesPool.length} elegível(is)`}
          </ContadorSelecao>
        </BarraSelecao>
        {carregando ? (
          <EmptyMsg>Carregando…</EmptyMsg>
        ) : ambientesPool.length === 0 ? (
          <EmptyMsg>
            Nenhum ambiente elegível — ambientes ficam elegíveis quando o
            Projeto Executivo é aprovado (e saem daqui enquanto estão num
            lote ativo).
          </EmptyMsg>
        ) : (
          <GradePool>
            {ambientesPool.map((a) => (
              <CardSelecionavel
                key={a.id}
                $selecionado={selecionados.has(a.id)}
              >
                <input
                  type="checkbox"
                  checked={selecionados.has(a.id)}
                  onChange={() => alternarSelecao(a.id)}
                  aria-label={`Selecionar ambiente ${a.numero}`}
                />
                <AmbienteCard
                  densidade="compact"
                  mostrarContexto
                  codigo={a.numero}
                  nome={a.nome}
                  pedidoNumero={a.pedido_numero}
                  cliente={a.cliente_nome}
                  faseLabel={FASE_INFO[a.fase]?.curto || a.fase}
                  pecasTotal={a.pecas_total}
                  progressoPct={a.progresso_pct}
                  bufferPct={a.buffer?.pct ?? 0}
                  bufferZona={a.buffer?.zona || "verde"}
                  congelado={a.congelado}
                  onClick={() => alternarSelecao(a.id)}
                />
              </CardSelecionavel>
            ))}
          </GradePool>
        )}
      </Card>

      <Card>
        <SectionTitle>Lotes</SectionTitle>
        {carregando ? (
          <EmptyMsg>Carregando…</EmptyMsg>
        ) : !lotes || lotes.length === 0 ? (
          <EmptyMsg>
            Nenhum lote criado ainda — selecione ambientes no pool acima e
            crie o primeiro.
          </EmptyMsg>
        ) : (
          <TabelaLotes>
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "17%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Lote</th>
                <th>Situação</th>
                <th>Origem</th>
                <th>Ambientes</th>
                <th>Requisição</th>
                <th>Sobra %</th>
                <th>Otimizado em</th>
              </tr>
            </thead>
            <tbody>
              {lotes.map((lote) => (
                <tr
                  key={lote.id}
                  onClick={() => navigate(`/producao/lotes/${lote.id}`)}
                  title={`${lote.numero} — abrir detalhe`}
                >
                  <td>
                    <Mono>{lote.numero}</Mono>
                  </td>
                  <td>
                    <Pill $tone={SITUACAO_LOTE_TONES[lote.situacao] || "neutral"}>
                      {lote.situacao_display
                        || SITUACAO_LOTE_LABELS[lote.situacao]
                        || lote.situacao}
                    </Pill>
                  </td>
                  <td title={lote.origem_display || lote.origem}>
                    {lote.origem_display || lote.origem}
                  </td>
                  <td>
                    <Mono>{lote.ambientes_count ?? 0}</Mono>
                  </td>
                  <td title={lote.requisicao_numero || ""}>
                    {lote.requisicao_numero ? (
                      <Mono>{lote.requisicao_numero}</Mono>
                    ) : (
                      "–"
                    )}
                  </td>
                  <td>
                    <Mono>
                      {lote.sobra_percentual != null
                        ? `${lote.sobra_percentual}%`
                        : "–"}
                    </Mono>
                  </td>
                  <td>
                    <Mono>{formatarDataHoraCurta(lote.otimizado_em)}</Mono>
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaLotes>
        )}
      </Card>

      <SlideOverPanel
        open={confirmandoCriacao}
        onClose={() => setConfirmandoCriacao(false)}
        title="Criar lote"
        subtitle={`${ambientesSelecionados.length} ambiente(s) selecionado(s)`}
        footer={
          <RodapePainel>
            <SecondaryButton
              type="button"
              onClick={() => setConfirmandoCriacao(false)}
            >
              Voltar
            </SecondaryButton>
            <AsyncButton
              disabled={ambientesSelecionados.length === 0}
              silentError
              successMessage="Lote criado."
              onClick={async () => {
                const lote = await criarLote(request, {
                  ambienteIds: ambientesSelecionados.map((a) => a.id),
                  origem,
                });
                setConfirmandoCriacao(false);
                navigate(`/producao/lotes/${lote.id}`);
              }}
            >
              Criar lote
            </AsyncButton>
          </RodapePainel>
        }
      >
        <ListaConfirmacao>
          {ambientesSelecionados.map((a) => (
            <li key={a.id} title={`${a.numero} · ${a.nome}`}>
              <Mono>{a.numero}</Mono> · {a.nome} — {a.pedido_numero}
              {a.cliente_nome ? ` · ${a.cliente_nome}` : ""}
            </li>
          ))}
        </ListaConfirmacao>
        <CampoForm>
          <RotuloCampo>Origem da otimização</RotuloCampo>
          <Select value={origem} onChange={(e) => setOrigem(e.target.value)}>
            {ORIGENS_LOTE.map((o) => (
              <option key={o.codigo} value={o.codigo}>
                {o.label}
              </option>
            ))}
          </Select>
          <DicaCampo>
            {origem === "DASHCUT"
              ? "O PharuCut real está em desenvolvimento — por enquanto a otimização integrada usa o provider mock (plano fictício para exercitar o fluxo)."
              : "Caminho de registro: o lote foi (ou será) otimizado fora do ERP — aqui fica o registro oficial de quais ambientes compõem o lote."}
          </DicaCampo>
        </CampoForm>
      </SlideOverPanel>
    </PageContainer>
  );
}
